import * as datefns from 'date-fns'
import { IssueEntity } from 'types/IssueEntity'
import { SourceType } from 'types/SourceType'
import { FormIssueEntity } from '../../pages/admin/types/FormIssueEntity'

/**
 * Type can be a list of types, e.g. ['Publication', 'Terms'], or a single type, e.g. 'Publication'
 */
export function hasSourceOfType(issue: Pick<IssueEntity, 'sources'>, type: SourceType | SourceType[]): boolean {
    // if type is not a list, make it a list
    if (!Array.isArray(type)) {
        type = [type]
    }
    return issue.sources && issue.sources.some((source) => type.includes(source.sourceType))
}

/**
 * False if v is null, undefined, or an empty string, true otherwise.
 * @param {*} v value to check
 */
function hasValue(v: unknown) {
    return v !== null && v !== undefined && v !== ''
}

/**
 * Types can be a list of types, e.g. ['Publication', 'Terms'], or a single type, e.g. 'Publication'
 */
function checkMandatorySourceTypes(
    issue: Pick<IssueEntity, 'sources'>,
    types: SourceType[] | SourceType,
    missingFields: string[],
) {
    // if type is not a list, make it a list
    if (!Array.isArray(types)) {
        types = [types]
    }
    types.forEach((type) => {
        if (!hasSourceOfType(issue, type)) {
            missingFields.push(`source:${type}`)
        }
    })
}

type CheckFunction = <T extends Record<string, unknown>>(
    issue: T,
    fields: Readonly<Array<Extract<keyof T, string>> | Extract<keyof T, string>>,
    missingFields: string[],
) => void

const checkMandatoryFields: CheckFunction = (issue, fields, missingFields) => {
    // if fields is not a list, make it a list
    const f = Array.isArray(fields) ? fields : [fields]
    f.forEach((field) => {
        if (!hasValue(issue[field])) {
            missingFields.push(field)
        }
    })
}

const checkMandatoryCheckboxes: CheckFunction = (issue, fields, missingFields) => {
    // if fields is not a list, make it a list
    const f = Array.isArray(fields) ? fields : [fields]

    f.forEach((field) => {
        if (!issue[field]) {
            missingFields.push(field)
        }
    })
}

const checkAtLeastOneField: CheckFunction = (issue, fields, missingFields) => {
    const f = Array.isArray(fields) ? fields : [fields]

    let found = false
    f.forEach((field) => {
        if (hasValue(issue[field])) {
            found = true
        }
    })
    if (!found) {
        missingFields.push(...f)
    }
}

export function checkInvestorsValues(
    issue: Pick<
        IssueEntity,
        | 'type'
        | 'investorsAfterTerms'
        | 'investorsAfterProspectus'
        | 'noInvestorsNamed'
        | 'investors'
        | 'sources'
        | 'noTermsInitially'
    >,
) {
    const missingFields: string[] = []

    if (issue.type === 'Guarantor fee issue') return []

    if (issue.type === 'Rights issue') {
        if (getRightsIssueStage(issue) >= 2) {
            checkMandatoryCheckboxes(issue, ['investorsAfterTerms'], missingFields)
        }

        if (getRightsIssueStage(issue) >= 3) {
            checkMandatoryCheckboxes(issue, ['investorsAfterProspectus'], missingFields)
        }
    }

    if ((issue.type === 'Rights issue' && getRightsIssueStage(issue) >= 3) || issue.type !== 'Rights issue') {
        if (!issue.noInvestorsNamed && !issue.investors?.length) {
            missingFields.push('investors')
        }
    }

    return missingFields
}

export const checkTermsValues = (issue: FormIssueEntity) => {
    switch (issue.type) {
        case 'IPO':
            return checkIpoTermsValues(issue)
        case 'Rights issue':
            return checkRightsIssueTermsValues(issue)
        case 'Directed issue':
            return checkDirectedIssueTermsFields(issue)
        case 'ABB Directed issue':
            return checkABBTermsFields(issue)
        case 'Non-cash issue':
            return checkNonCashTermsFields(issue)
    }

    return []
}

export const checkImportantDatesValues = (
    issue: Pick<
        FormIssueEntity,
        | 'recordDate'
        | 'incRightsDate'
        | 'ipoTradingDate'
        | 'exRightsDate'
        | 'rightsStartDate'
        | 'rightsEndDate'
        | 'subStartDate'
        | 'subEndDate'
        | 'type'
        | 'sources'
        | 'noTermsInitially'
    >,
) => {
    switch (issue.type) {
        case 'IPO':
            return checkIpoImportantDatesValues(issue)
        case 'Rights issue':
            return checkRightsIssueImportDatesValues(issue)
    }

    return []
}

const checkIpoImportantDatesValues = (issue: Pick<IssueEntity, 'ipoTradingDate' | 'subStartDate' | 'subEndDate'>) => {
    const missingFields: string[] = []

    checkMandatoryFields(issue, ['ipoTradingDate', 'subStartDate', 'subEndDate'], missingFields)

    return missingFields
}

const checkIpoTermsValues = (
    issue: Pick<
        IssueEntity,
        | 'ipoData'
        | 'offeredUnits'
        | 'sharesInUnit'
        | 'noOverAllotment'
        | 'overAllotmentSize'
        | 'overAllotmentSizeSecondary'
        | 'noSecondaryOffering'
        | 'secondaryOffering'
    >,
) => {
    const missingFields: string[] = []

    if (issue?.ipoData) {
        checkMandatoryFields(
            issue.ipoData,
            [
                'companyName',
                'instrumentSymbol',
                'instrumentIsin',
                'countryCode',
                'currency',
                'companyTotalShares',
                'marketplaceId',
            ],
            missingFields,
        )
    }

    checkMandatoryFields(issue, ['ipoData', 'offeredUnits', 'sharesInUnit'], missingFields)

    checkAtLeastOneField(issue, ['noOverAllotment', 'overAllotmentSize', 'overAllotmentSizeSecondary'], missingFields)
    checkAtLeastOneField(issue, ['noSecondaryOffering', 'secondaryOffering'], missingFields)

    return missingFields
}

/**
 * Checks presence of required fields for the prospectus stage.
 */
const checkIpoProspectusValues = (
    issue: Pick<
        IssueEntity,
        | 'type'
        | 'advisors'
        | 'noFinancialAdvisor'
        | 'noLegalAdvisor'
        | 'noIssuingAgent'
        | 'sources'
        | 'noTermsInitially'
        | 'noGuarantors'
        | 'fees'
        | 'investorsAfterTerms'
        | 'investorsAfterProspectus'
        | 'noInvestorsNamed'
        | 'investors'
    > & { ipoData: NonNullable<IssueEntity['ipoData']> },
) => {
    const missingFields: string[] = []
    missingFields.push(...checkAdvisors(issue))
    missingFields.push(...checkFeeStructureValues(issue))
    missingFields.push(...checkInvestorsValues(issue))

    checkMandatoryFields(issue.ipoData, ['instrumentIsin'], missingFields)

    return missingFields
}

export const checkOutcomeValues = (
    issue: Pick<
        IssueEntity,
        | 'type'
        | 'unitSubTotal'
        | 'secondarySub'
        | 'secondaryOffering'
        | 'noSecondaryOffering'
        | 'offeredUnits'
        | 'overAllotmentSize'
        | 'overAllotmentSizeSecondary'
        | 'noGuarantors'
        | 'unitSubPrice'
        | 'unitSubGuarantor'
        | 'unitPctSubGuarantor'
        | 'overAllotmentAllocation'
        | 'overAllotmentAllocationSecondary'
    >,
) => {
    const missingFields: string[] = []
    switch (issue.type) {
        case 'IPO': {
            return checkIpoOutcomeValues(issue)
        }

        case 'Rights issue': {
            return checkRightsIssueOutcomeValues(issue)
        }

        case 'Guarantor fee issue':
        case 'Non-cash issue':
            checkMandatoryFields(issue, ['unitSubTotal'], missingFields)
            break
        case 'ABB Directed issue':
            checkMandatoryFields(issue, ['unitSubTotal'], missingFields)

            if (!issue.noSecondaryOffering) {
                checkMandatoryFields(issue, ['secondarySub'], missingFields)
            }
            break
        case 'Directed issue': {
            checkMandatoryFields(issue, ['unitSubTotal'], missingFields)

            if (!issue.noSecondaryOffering) {
                checkMandatoryFields(issue, ['secondaryOffering', 'secondarySub'], missingFields)
            }
        }
    }

    return missingFields
}

const checkIpoOutcomeValues = (
    issue: Pick<
        IssueEntity,
        | 'unitSubTotal'
        | 'unitSubPrice'
        | 'unitSubGuarantor'
        | 'unitPctSubGuarantor'
        | 'noGuarantors'
        | 'overAllotmentAllocation'
        | 'offeredUnits'
        | 'overAllotmentSize'
        | 'overAllotmentSizeSecondary'
        | 'noSecondaryOffering'
        | 'secondarySub'
        | 'overAllotmentAllocationSecondary'
    >,
) => {
    const missingFields: string[] = []

    if ((issue.offeredUnits ?? 0) > 0) {
        checkMandatoryFields(issue, ['unitSubTotal'], missingFields)
    }

    checkMandatoryFields(issue, ['unitSubPrice'], missingFields)

    if (!issue.noGuarantors) {
        checkAtLeastOneField(issue, ['unitSubGuarantor', 'unitPctSubGuarantor'], missingFields)
    }

    if (issue.overAllotmentSize) {
        checkMandatoryFields(issue, ['overAllotmentAllocation'], missingFields)
    }
    if (issue.overAllotmentSizeSecondary) {
        checkMandatoryFields(issue, ['overAllotmentAllocationSecondary'], missingFields)
    }

    if (!issue.noSecondaryOffering && !hasValue(issue.secondarySub)) {
        missingFields.push('secondarySub')
    }
    return missingFields
}

const checkRightsIssueImportDatesValues = (
    issue: Pick<
        IssueEntity,
        | 'recordDate'
        | 'incRightsDate'
        | 'exRightsDate'
        | 'rightsStartDate'
        | 'rightsEndDate'
        | 'subStartDate'
        | 'subEndDate'
        | 'type'
        | 'sources'
        | 'noTermsInitially'
    >,
) => {
    const missingFields: string[] = []
    const dateFields = [
        'recordDate',
        'incRightsDate',
        'exRightsDate',
        'rightsStartDate',
        'rightsEndDate',
        'subStartDate',
        'subEndDate',
    ] as const

    if (getRightsIssueStage(issue) < 2) return []

    checkMandatoryFields(issue, dateFields, missingFields)

    return missingFields
}

/**
 * Checks presence of required fields for the terms stage, i.e:
 * - sharesRequired
 * - rightsReceived
 * - rightsRequired
 * - unitsReceived
 * - sharesInUnit
 * - unitSubPrice
 * - offeredUnits
 */
const checkRightsIssueTermsValues = <
    T extends Pick<
        IssueEntity,
        | 'noTermsInitially'
        | 'sharesRequired'
        | 'rightsRequired'
        | 'rightsReceived'
        | 'unitsReceived'
        | 'sharesInUnit'
        | 'unitSubPrice'
        | 'offeredUnits'
        | 'type'
        | 'sources'
        | 'noOverAllotment'
        | 'overAllotmentSize'
        | 'secondaryOffering'
        | 'noSecondaryOffering'
    >,
>(
    issue: T,
) => {
    const missingFields: string[] = []
    const termsFields = [
        'sharesRequired',
        'rightsRequired',
        'rightsReceived',
        'unitsReceived',
        'sharesInUnit',
        'unitSubPrice',
        'offeredUnits',
    ] as Array<Extract<keyof T, string>>

    if (getRightsIssueStage(issue) < 2) return []

    checkMandatoryFields(issue, termsFields, missingFields)
    if (!issue.noOverAllotment && !hasValue(issue.overAllotmentSize)) {
        missingFields.push('overAllotmentSize')
    }
    if (!issue.noSecondaryOffering && !hasValue(issue.secondaryOffering)) {
        missingFields.push('secondaryOffering')
    }
    return missingFields
}

export const checkFeeStructureValues = (
    issue: Pick<FormIssueEntity, 'type' | 'noGuarantors' | 'fees' | 'noTermsInitially' | 'sources'>,
) => {
    switch (issue.type) {
        case 'Rights issue':
        case 'IPO': {
            if (issue.type === 'Rights issue' && getRightsIssueStage(issue) < 3) return []

            if (!issue.noGuarantors) {
                if (!hasValue(issue.fees) || issue.fees.length === 0) {
                    return ['fees']
                }
            }
        }
    }

    return []
}

/**
 * Checks presence of required fields for the prospectus stage.
 */
const checkRightsIssueProspectusValues = (issue: FormIssueEntity) => {
    const missingFields: string[] = []
    missingFields.push(...checkAdvisors(issue))
    missingFields.push(...checkFeeStructureValues(issue))
    missingFields.push(...checkInvestorsValues(issue))
    return missingFields
}

const checkRightsIssueOutcomeValues = (issue: IssueEntity) => {
    const missingFields: string[] = []

    if (getRightsIssueStage(issue) < 4) return []

    checkMandatoryFields(issue, ['unitSubTotal'], missingFields)
    checkAtLeastOneField(issue, ['unitSubWithRights', 'unitPctSubWithRights'], missingFields)
    checkAtLeastOneField(issue, ['unitSubWithoutRights', 'unitPctSubWithoutRights'], missingFields)

    if (!issue.noGuarantors) {
        checkAtLeastOneField(issue, ['unitSubGuarantor', 'unitPctSubGuarantor'], missingFields)
    }
    if (!issue.noSecondaryOffering && !hasValue(issue.secondarySub)) {
        missingFields.push('secondarySub')
    }
    if (!issue.noOverAllotment && !hasValue(issue.overAllotmentAllocation)) {
        missingFields.push('overAllotmentAllocation')
    }
    return missingFields
}

export const checkAdvisors = (
    issue: Pick<
        IssueEntity,
        | 'type'
        | 'advisors'
        | 'noFinancialAdvisor'
        | 'noLegalAdvisor'
        | 'noIssuingAgent'
        | 'sources'
        | 'noTermsInitially'
    >,
) => {
    const missingFields: string[] = []

    if (issue.type === 'Rights issue' && getRightsIssueStage(issue) < 3) return []

    if (!issue.noFinancialAdvisor) {
        if (!issue.advisors || !issue.advisors.some((advisor) => advisor.type === 'financial_advisor')) {
            missingFields.push('financialAdvisor')
        }
    }
    if (!issue.noLegalAdvisor) {
        if (!issue.advisors || !issue.advisors.some((advisor) => advisor.type === 'legal_advisor')) {
            missingFields.push('legalAdvisor')
        }
    }
    if (!issue.noIssuingAgent) {
        if (!issue.advisors || !issue.advisors.some((advisor) => advisor.type === 'issuing_agent')) {
            missingFields.push('issuingAgent')
        }
    }

    return missingFields
}

function getRightsIssueStage(issue: Pick<IssueEntity, 'sources' | 'type' | 'noTermsInitially'>) {
    if (!issue.sources) throw new Error('Issue must have sources to determine stage.')
    let stage = 0
    if (hasSourceOfType(issue, 'Outcome')) {
        stage = 4
    } else if (hasSourceOfType(issue, ['Prospectus', 'Memorandum', 'InformationDocument'])) {
        stage = 3
    } else if (hasSourceOfType(issue, 'Terms') || (issue.type === 'Rights issue' && !issue.noTermsInitially)) {
        stage = 2
    } else if (hasSourceOfType(issue, 'Publication')) {
        stage = 1
    }

    return stage
}

const checkIpoAndRightsIssueSources = (issue: Pick<IssueEntity, 'sources'>) => {
    const hasSomeOf: SourceType[] = ['Prospectus', 'Memorandum', 'InformationDocument']
    if (!hasSourceOfType(issue, hasSomeOf)) {
        return [hasSomeOf.map((t) => `source:${t}`).join(', ')]
    }

    return []
}

/**
 * Returns an object with the following keys:
 * - stage: 'initial', 'published', 'terms', 'prospect', 'outcome'
 * - status: 'complete', 'incomplete'
 * - missingFields: Array of field names that are missing
 */
function calculateIPOStatus(
    issue: Pick<
        IssueEntity,
        | 'ipoData'
        | 'offeredUnits'
        | 'sharesInUnit'
        | 'noOverAllotment'
        | 'overAllotmentSize'
        | 'overAllotmentSizeSecondary'
        | 'noSecondaryOffering'
        | 'secondaryOffering'
        | 'ipoTradingDate'
        | 'subStartDate'
        | 'subEndDate'
        | 'type'
        | 'advisors'
        | 'noFinancialAdvisor'
        | 'noLegalAdvisor'
        | 'noIssuingAgent'
        | 'sources'
        | 'noTermsInitially'
        | 'noGuarantors'
        | 'fees'
        | 'investorsAfterTerms'
        | 'investorsAfterProspectus'
        | 'noInvestorsNamed'
        | 'investors'
        | 'unitSubTotal'
        | 'unitSubPrice'
        | 'unitSubGuarantor'
        | 'unitPctSubGuarantor'
        | 'overAllotmentAllocation'
        | 'secondarySub'
        | 'overAllotmentAllocationSecondary'
    > & { ipoData: NonNullable<IssueEntity['ipoData']> },
) {
    let missingFields: string[] = []

    missingFields = missingFields.concat(checkIpoTermsValues(issue))
    missingFields = missingFields.concat(checkIpoImportantDatesValues(issue))
    missingFields = missingFields.concat(checkIpoProspectusValues(issue))
    missingFields = missingFields.concat(checkIpoOutcomeValues(issue))
    missingFields = missingFields.concat(checkIpoAndRightsIssueSources(issue))

    return { stage: 10, status: missingFields.length === 0 ? 'complete' : 'incomplete', missingFields }
}

function calculateRightsIssueStatus(issue: Pick<IssueEntity, 'type' | 'noTermsInitially' | 'sources'>) {
    let status = 'incomplete'
    let missingFields: string[] = []
    let stage = getRightsIssueStage(issue)

    missingFields = missingFields.concat(checkRightsIssueTermsValues(issue))
    missingFields = missingFields.concat(checkRightsIssueImportDatesValues(issue))
    missingFields = missingFields.concat(checkRightsIssueProspectusValues(issue))
    missingFields = missingFields.concat(checkRightsIssueOutcomeValues(issue))

    if (stage >= 4) {
        missingFields = missingFields.concat(checkIpoAndRightsIssueSources(issue))
    }

    if (missingFields.length === 0) {
        status = 'complete'
        if (stage === 4) {
            stage = 10 // if outcome is complete, the issue is complete
        }
    }
    return { stage, status, missingFields }
}

const checkDirectedIssueTermsFields = (issue: Pick<IssueEntity, 'unitSubPrice' | 'offeredUnits' | 'abbTarget'>) => {
    const missingFields: string[] = []
    checkMandatoryFields(issue, ['unitSubPrice'], missingFields)
    checkAtLeastOneField(issue, ['offeredUnits', 'abbTarget'], missingFields)

    return missingFields
}

function calculateDirectedIssueStatus(
    issue: Pick<
        IssueEntity,
        | 'unitSubPrice'
        | 'offeredUnits'
        | 'abbTarget'
        | 'type'
        | 'advisors'
        | 'noLegalAdvisor'
        | 'noFinancialAdvisor'
        | 'noIssuingAgent'
        | 'noTermsInitially'
        | 'sources'
        | 'investors'
        | 'investorsAfterProspectus'
        | 'investorsAfterTerms'
        | 'noInvestorsNamed'
    >,
) {
    const stage = 10
    const missingFields: string[] = []
    let status = 'complete'
    missingFields.push(...checkAdvisors(issue))
    missingFields.push(...checkDirectedIssueTermsFields(issue))
    missingFields.push(...checkOutcomeValues(issue))

    checkMandatorySourceTypes(issue, 'Publication', missingFields)
    missingFields.push(...checkInvestorsValues(issue))
    if (missingFields.length > 0) {
        status = 'incomplete'
    }
    return { stage, status, missingFields }
}

const checkABBTermsFields = (
    issue: Pick<
        IssueEntity,
        'unitSubPrice' | 'offeredUnits' | 'abbTarget' | 'secondaryOffering' | 'noSecondaryOffering'
    >,
) => {
    const missingFields: string[] = []
    checkMandatoryFields(issue, ['unitSubPrice'], missingFields)
    checkAtLeastOneField(issue, ['offeredUnits', 'abbTarget'], missingFields)

    if (!issue.noSecondaryOffering) {
        checkMandatoryFields(issue, ['secondaryOffering'], missingFields)
    }

    return missingFields
}

function calculateABBIssueStatus(
    issue: Pick<
        IssueEntity,
        | 'type'
        | 'advisors'
        | 'noFinancialAdvisor'
        | 'noLegalAdvisor'
        | 'noIssuingAgent'
        | 'sources'
        | 'noTermsInitially'
        | 'abbTarget'
        | 'offeredUnits'
        | 'secondaryOffering'
        | 'unitSubPrice'
        | 'noInvestorsNamed'
        | 'investors'
        | 'investorsAfterProspectus'
        | 'investorsAfterTerms'
        | 'noSecondaryOffering'
    >,
) {
    const stage = 10
    const missingFields: string[] = []
    let status = 'complete'
    missingFields.push(...checkAdvisors(issue))
    missingFields.push(...checkABBTermsFields(issue))
    missingFields.push(...checkOutcomeValues(issue))

    missingFields.push(...checkInvestorsValues(issue))
    checkMandatorySourceTypes(issue, ['Publication', 'Outcome'], missingFields)
    if (missingFields.length > 0) {
        status = 'incomplete'
    }
    return { stage, status, missingFields }
}

const checkNonCashTermsFields = (
    issue: Pick<IssueEntity, 'unitSubPrice' | 'noncashConsideration' | 'offeredUnits' | 'abbTarget'>,
) => {
    const missingFields: string[] = []

    checkMandatoryFields(issue, ['unitSubPrice', 'noncashConsideration'], missingFields)
    checkAtLeastOneField(issue, ['offeredUnits', 'abbTarget'], missingFields)

    return missingFields
}

function calculateNonCashIssueStatus(
    issue: Pick<
        IssueEntity,
        | 'type'
        | 'advisors'
        | 'noFinancialAdvisor'
        | 'noLegalAdvisor'
        | 'noIssuingAgent'
        | 'sources'
        | 'noTermsInitially'
        | 'unitSubPrice'
        | 'noncashConsideration'
        | 'offeredUnits'
        | 'abbTarget'
        | 'investorsAfterTerms'
        | 'investorsAfterProspectus'
        | 'noInvestorsNamed'
        | 'investors'
    >,
) {
    const stage = 10
    const missingFields: string[] = []
    let status = 'complete'
    missingFields.push(...checkAdvisors(issue))
    missingFields.push(...checkNonCashTermsFields(issue))
    missingFields.push(...checkOutcomeValues(issue))
    missingFields.push(...checkInvestorsValues(issue))
    checkMandatorySourceTypes(issue, 'Publication', missingFields)
    if (missingFields.length > 0) {
        status = 'incomplete'
    }
    return { stage, status, missingFields }
}

function checkGuarantorIssueLinks(issue: Pick<FormIssueEntity, 'linkedIssues' | 'id'>, missingFields: string[]) {
    const groups = issue.linkedIssues || []

    if (
        groups.length != 1 ||
        groups[0].issues.length !== 1 ||
        !groups.some((group) => group.issues.some((i) => ['Rights issue', 'IPO'].includes(i.type)))
    ) {
        missingFields.push('guarantorGroup')
    }
}

function calculateGuarantorFeeIssueStatus(issue: Pick<IssueEntity, 'linkedIssues' | 'sources' | 'type'>) {
    const stage = 10
    const missingFields: string[] = []
    let status = 'complete'
    checkGuarantorIssueLinks(issue, missingFields)
    missingFields.push(...checkOutcomeValues(issue))
    checkMandatorySourceTypes(issue, 'Publication', missingFields)
    if (missingFields.length > 0) {
        status = 'incomplete'
    }
    return { stage, status, missingFields }
}

export function calculateIssueStatus(
    issue: Pick<
        IssueEntity,
        | 'noTermsInitially'
        | 'sharesRequired'
        | 'rightsRequired'
        | 'rightsReceived'
        | 'unitsReceived'
        | 'sharesInUnit'
        | 'unitSubPrice'
        | 'offeredUnits'
        | 'type'
        | 'sources'
        | 'noOverAllotment'
        | 'overAllotmentSize'
        | 'secondaryOffering'
        | 'noSecondaryOffering'
        | 'advisors'
        | 'noFinancialAdvisor'
        | 'noLegalAdvisor'
        | 'investors'
        | 'noIssuingAgent'
        | 'abbTarget'
        | 'investorsAfterProspectus'
        | 'investorsAfterTerms'
        | 'noInvestorsNamed'
        | 'noncashConsideration'
        | 'ipoData'
        | 'overAllotmentSizeSecondary'
        | 'ipoTradingDate'
        | 'subStartDate'
        | 'subEndDate'
        | 'noGuarantors'
        | 'fees'
        | 'unitSubTotal'
        | 'unitSubGuarantor'
        | 'unitPctSubGuarantor'
        | 'overAllotmentAllocation'
        | 'secondarySub'
        | 'overAllotmentAllocationSecondary'
    >,
) {
    if (!issue) return null
    if (hasSourceOfType(issue, 'Cancellation')) {
        return { stage: 5, status: 'complete', missingFields: [] }
    }
    let response: { stage: number; status: string; missingFields: string[] } | null = null
    if (issue.type === 'Rights issue') {
        response = calculateRightsIssueStatus(issue)
    } else if (issue.type === 'Directed issue') {
        response = calculateDirectedIssueStatus(issue)
    } else if (issue.type === 'ABB Directed issue') {
        response = calculateABBIssueStatus(issue)
    } else if (issue.type === 'Non-cash issue') {
        response = calculateNonCashIssueStatus(issue)
    } else if (issue.type === 'Guarantor fee issue') {
        response = calculateGuarantorFeeIssueStatus(issue)
    } else if (issue.type === 'IPO') {
        response = calculateIPOStatus(issue)
    }

    if (!response) return response

    // make sure there are no duplicates in missingFields
    response.missingFields = Array.from(new Set(response.missingFields))
    let hideUntil: Date | null = null

    if (issue.statusHideUntil) {
        // use datefns to check if the issue.statusHideUntil date is in the future
        const statusHideUntil = new Date(issue.statusHideUntil)
        if (datefns.isBefore(new Date(), statusHideUntil)) {
            hideUntil = statusHideUntil
        } else {
            hideUntil = null
        }
    }
    return { ...response, hideUntil }
}
